/**
 * Table of registered AI models: identity/routing columns plus the four
 * nullable capability columns, an enabled toggle, and edit/delete actions.
 */

import { useState, type ReactElement } from 'react';
import type { AiModel } from '@services/aiModelsService';
import { useUpdateAiModel, useDeleteAiModel } from '../hooks/useAiModels';
import { AiModelPriceCell } from './AiModelPriceCell';
import { AiModelPriceDialog } from './AiModelPriceDialog';
import { formatNullableNumber, formatNullableBoolean, UNKNOWN_MARKER } from '../utils/capabilityFormat';

interface AiModelTableProps {
  models: AiModel[];
  onEdit: (model: AiModel) => void;
}

export function AiModelTable({ models, onEdit }: AiModelTableProps): ReactElement {
  const updateModel = useUpdateAiModel();
  const deleteModel = useDeleteAiModel();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [priceModel, setPriceModel] = useState<AiModel | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleToggleEnabled = (model: AiModel): void => {
    setPendingId(model.id);
    updateModel.mutate(
      { modelId: model.model_id, body: { enabled: !model.enabled } },
      { onSettled: () => { setPendingId(null); } },
    );
  };

  const handleDelete = (model: AiModel): void => {
    if (!window.confirm(`Delete "${model.model_id}"?`)) return;
    setDeleteError(null);
    setPendingId(model.id);
    deleteModel.mutate(model.model_id, {
      onError: () => { setDeleteError(`Failed to delete "${model.model_id}".`); },
      onSettled: () => { setPendingId(null); },
    });
  };

  return (
    <div className="ai-models-table">
      {deleteError && (
        <div className="dialog__error" role="alert">
          {deleteError}
        </div>
      )}
      <table className="feature-table__table">
        <thead>
          <tr>
            <th>Model ID</th>
            <th>Upstream</th>
            <th>API</th>
            <th>Upstream model ID</th>
            <th>Provider</th>
            <th>Region</th>
            <th>Enabled</th>
            <th>Context window</th>
            <th>Max output tokens</th>
            <th>Prompt caching</th>
            <th>Extended thinking</th>
            <th>Price (in / out per 1M)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {models.length === 0 ? (
            <tr>
              <td colSpan={13} className="feature-table__empty">
                No models registered.
              </td>
            </tr>
          ) : (
            models.map((model) => (
              <tr key={model.id}>
                <td>{model.model_id}</td>
                <td>{model.upstream}</td>
                <td>{model.api}</td>
                <td>{model.upstream_model_id}</td>
                <td>{model.provider}</td>
                <td>{model.region ?? UNKNOWN_MARKER}</td>
                <td>
                  <button
                    type="button"
                    className={`feature-table__toggle ${model.enabled ? 'feature-table__toggle--active' : ''}`}
                    onClick={() => { handleToggleEnabled(model); }}
                    disabled={pendingId === model.id}
                    aria-label={`${model.enabled ? 'Disable' : 'Enable'} ${model.model_id}`}
                  >
                    {model.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </td>
                <td>{formatNullableNumber(model.context_window)}</td>
                <td>{formatNullableNumber(model.max_output_tokens)}</td>
                <td>{formatNullableBoolean(model.supports_prompt_caching)}</td>
                <td>{formatNullableBoolean(model.supports_extended_thinking)}</td>
                <td>
                  <AiModelPriceCell
                    model={model}
                    onEdit={() => { setPriceModel(model); }}
                  />
                </td>
                <td>
                  <div className="feature-table__actions-cell">
                    <button
                      type="button"
                      className="feature-table__edit-btn"
                      onClick={() => { onEdit(model); }}
                      aria-label={`Edit ${model.model_id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="feature-table__edit-btn feature-table__edit-btn--danger"
                      onClick={() => { handleDelete(model); }}
                      disabled={pendingId === model.id}
                      aria-label={`Delete ${model.model_id}`}
                    >
                      {pendingId === model.id && deleteModel.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {priceModel && (
        <AiModelPriceDialog model={priceModel} onClose={() => { setPriceModel(null); }} />
      )}
    </div>
  );
}
